import React, { useRef, useState } from 'react';
import { KeyboardAvoidingView, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useResponsiveness from '@/hooks/useResponsiveness';
import useMacModalFocus from '@/hooks/useMacModalFocus';

export default function IdeaDetail({ idea, onClose, onTry }) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [prompt, setPrompt] = useState(idea.prompt);
  const isMacOS = Platform.OS === 'macos';
  const sheetRef = useRef(null);
  const syncMacFocus = useMacModalFocus(sheetRef, isMacOS, onClose);
  const mode = idea.mode === 'work' ? 'Work' : 'Chat';
  const content = (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={[styles.overlay, { padding: isXS ? 12 : 32, paddingTop: Math.max(insets.top, 12), paddingBottom: Math.max(insets.bottom, 12) }]}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessible={false} {...(Platform.OS === 'web' ? { tabIndex: -1 } : {})} />
      <View ref={sheetRef} accessibilityViewIsModal
        {...(isMacOS ? { focusable: true, enableFocusRing: false, onLayout: syncMacFocus } : {})}
        style={[styles.sheet, { backgroundColor: colors.solid_color_primary }]}>
        <View style={styles.top}>
          <View style={[styles.icon, { backgroundColor: palette.preview }]}><Icon name={idea.icon || 'lightbulb'} size={26} color={palette.ink} /></View>
          <Pressable accessibilityRole="button" accessibilityLabel="Close idea" onPress={onClose} style={[styles.close, { backgroundColor: palette.preview }]}>
            <Icon name="xmark" size={16} color={palette.ink} />
          </Pressable>
        </View>
        <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={styles.body}>
          <Text size="s" color="secondary">{mode}{idea.personalized ? ' · Inspired by your memory' : ''}</Text>
          <Text tag="h2" accessibilityRole="header" weight="semibold" style={styles.title}>{idea.title}</Text>
          {!!idea.description && <Text color="secondary" style={styles.description}>{idea.description}</Text>}
          <View style={[styles.result, { borderColor: palette.border }]}>
            <Icon name={idea.mode === 'work' ? 'files' : 'comment'} size={17} color={palette.icon} />
            <Text size="s" color="secondary" style={{ flex: 1 }}>{idea.result}</Text>
          </View>
          <Text weight="medium" size="s" style={{ marginBottom: 10 }}>Make it yours</Text>
          <TextInput accessibilityLabel="Idea prompt" multiline value={prompt} onChangeText={setPrompt}
            style={[styles.prompt, { color: colors.text_color_primary, backgroundColor: palette.surface, borderColor: palette.border }]}
            textAlignVertical="top" />
        </ScrollView>
        <View style={[styles.footer, { borderColor: palette.border }]}>
          <Text size="xs" color="secondary" style={{ textAlign: 'center' }}>{idea.mode === 'work' ? 'Add your details. Send when you’re ready.' : 'Add your details, then start the conversation.'}</Text>
          <Pressable accessibilityRole="button" accessibilityLabel={`Try in ${mode}`} disabled={!prompt.trim()}
            accessibilityState={{ disabled: !prompt.trim() }} onPress={() => onTry(idea, prompt)}
            style={({ pressed }) => [styles.primary, { backgroundColor: palette.ink, opacity: !prompt.trim() ? 0.4 : pressed ? 0.8 : 1 }]}>
            <Text weight="semibold" style={{ color: palette.onInk }}>Try in {mode}</Text>
            <Icon name="arrow-right" size={15} color={palette.onInk} />
          </Pressable>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
  // RN macOS does not register RCTModalHostView. Mounting a native Modal
  // corrupts its child hierarchy and crashes when the detail is removed.
  // Keep the sheet in the app window, with the shared Mac focus/Escape scope.
  if (isMacOS) return <View style={styles.macHost} accessibilityLabel={idea.title}>{content}</View>;
  return (
    <Modal transparent animationType={Platform.OS === 'web' ? 'none' : 'fade'} visible onRequestClose={onClose} accessibilityLabel={idea.title}>
      {content}
    </Modal>
  );
}

const styles = StyleSheet.create({
  macHost: { ...StyleSheet.absoluteFillObject, zIndex: 10002 },
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.32)' },
  sheet: { width: '100%', maxWidth: 560, maxHeight: '100%', borderRadius: 24, overflow: 'hidden', flexShrink: 1 },
  top: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 24, paddingBottom: 16 },
  icon: { width: 56, height: 56, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  close: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  body: { paddingHorizontal: 24, paddingBottom: 24 },
  title: { fontSize: 29, lineHeight: 35, letterSpacing: -0.8, marginTop: 8, marginBottom: 10 },
  description: { fontSize: 15, lineHeight: 23 },
  result: { flexDirection: 'row', gap: 10, alignItems: 'center', paddingVertical: 18, marginBottom: 20, borderBottomWidth: 1 },
  prompt: { borderWidth: 1, borderRadius: 14, padding: 16, fontSize: 15, lineHeight: 23, minHeight: 150, maxHeight: 230 },
  footer: { padding: 20, gap: 12, borderTopWidth: 1 },
  primary: { minHeight: 48, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
});
